import { default as http } from "../rest/http";

// Product Support debug endpoints (transaction-service). All are GET with query
// params, SUPER_ADMIN only. Types below mirror the locked API contract exactly —
// the UI must render from these responses verbatim and never recompute values.
const BASE_PATH = "/transaction-service/admin/support-debug";

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

// One recurring bill as returned by RecurringTransaction.to_dict() (camelCase).
export interface SupportDebugBill {
  id: number;
  categoryType: string | null;
  finleyCategoryName: string | null;
  finleyCategory: string | null;
  description: string | null;
  merchantName: string | null;
  logoUrl: string | null;
  amount: number;
  monthlyAmount: number;
  frequency: string | null;
  nextBillDate: string | null; // "YYYY-MM-DD"
  lastBillDate: string | null; // "YYYY-MM-DD"
}

export interface FormulaWindow {
  start: string; // "YYYY-MM-DD"
  end: string; // "YYYY-MM-DD"
}

// One row of a formula breakdown. Values are COPIED from the backend's raw
// result — never recomputed client-side.
export interface FormulaComponent {
  key: string;
  label: string;
  value: number;
  window?: FormulaWindow | null;
  description?: string;
}

export interface FormulaResult {
  key: string;
  value: number;
}

export interface SupportDebugFormula {
  expression: string;
  components: FormulaComponent[];
  result: FormulaResult;
}

// ---------------------------------------------------------------------------
// 1) GET .../upcoming-bills
// ---------------------------------------------------------------------------

export interface UpcomingBillsResponse {
  userId: string;
  windowStart: string; // "YYYY-MM-DD" (today)
  windowEnd: string; // "YYYY-MM-DD" (today + days)
  days: number;
  count: number;
  totalAmount: number;
  totalMonthlyAmount: number;
  bills: SupportDebugBill[];
}

// ---------------------------------------------------------------------------
// 2) GET .../safe-to-spend
// ---------------------------------------------------------------------------

// SpendingPlanService.get_safe_to_spend(user_id) dict, verbatim.
export interface SafeToSpendRaw {
  income: number;
  upcomingBills: number;
  safeToSpend: number;
  alreadySpent: number;
  upcomingBillsList: SupportDebugBill[];
  hasActiveRecurringBills: boolean;
  upcomingBillsForNext30Days: number;
  upcomingBillsListForNext30Days: SupportDebugBill[];
}

export interface SafeToSpendResponse {
  userId: string;
  asOf: string; // "YYYY-MM-DD"
  formula: SupportDebugFormula;
  raw: SafeToSpendRaw;
}

// ---------------------------------------------------------------------------
// 3) GET .../cashflow-shortfall
// ---------------------------------------------------------------------------

export type SpendingHealthState =
  | "in_the_clear"
  | "eating_into_goals"
  | "getting_tight"
  | "slight_shortfall"
  | "large_shortfall";

export interface StateLadderRow {
  state: SpendingHealthState;
  rule: string;
  matched: boolean;
}

export interface PayPeriod {
  start: string; // "YYYY-MM-DD"
  end: string; // "YYYY-MM-DD"
  daysElapsed: number;
  daysRemaining: number;
}

export interface DailySpendDay {
  date: string; // "YYYY-MM-DD"
  spent: number;
}

// What the Spending Health widget derives (camelCase). The optional fields are
// present ONLY when eligible=true.
export interface CashflowWidget {
  eligible: boolean;
  skipReason: string | null;
  fallback: "safe_to_spend" | null;
  state?: SpendingHealthState;
  shortfallValue?: number;
  threshold?: number;
  thresholdPct?: number;
  heroNumber?: string; // contains U+2212 minus — render verbatim
  header?: string;
  subtext?: string;
  aiChatPrompt?: string;
  infoTooltip?: string;
  goalName?: string | null;
  goalTargetThisPeriod?: number | null;
  payPeriod?: PayPeriod;
  projectedIncome?: number;
  currentSpend?: number;
  billsRemaining?: number;
  billsRemainingList?: SupportDebugBill[];
  dailySpendLast7Days?: DailySpendDay[];
  stateLadder?: StateLadderRow[];
}

export type CategoryProjectionSkipReason =
  | "day_one_no_history"
  | "no_spend_in_period"
  | "limit_consumed_by_bills"
  | "no_history"
  | "nothing_remaining";

export interface CategoryDetail {
  catg_id: number;
  category_name: string | null;
  kind: "elastic" | "inelastic";
  elasticity: number | null;
  spent: number;
  burn_rate: number | null;
  monthly_limit: number | null;
  monthly_recurring: number | null;
  discretionary_limit: number | null;
  pay_period_cap: number | null;
  projected: number;
  source: "burn_rate" | "configured_limit" | "historical_average";
  skip_reason: CategoryProjectionSkipReason | null;
}

// CashflowShortfallResult.to_dict() verbatim (snake_case) plus category_detail.
export interface CashflowAlgorithm {
  triggered: boolean;
  shortfall_value: number;
  pay_period_start: string | null;
  pay_period_end: string | null;
  days_elapsed: number;
  days_remaining: number;
  projected_income: number;
  bills_paid: number;
  bills_remaining: number;
  current_spend: number;
  remaining_projected_elastic: number;
  remaining_projected_inelastic: number;
  eligibility_skip_reason: string | null;
  category_detail: CategoryDetail[];
}

export interface CashflowShortfallResponse {
  userId: string;
  asOf: string; // "YYYY-MM-DD"
  widget: CashflowWidget;
  algorithm: CashflowAlgorithm;
  billsPaidList: SupportDebugBill[];
  formula: SupportDebugFormula | null; // null when ineligible
}

// ---------------------------------------------------------------------------
// User lookup (existing master-data endpoints — v1, snake_case)
// ---------------------------------------------------------------------------

// Row of GET /master-data/users?email=... (no payday fields on the list shape).
export interface SupportDebugUserSearchResult {
  id: string;
  email: string;
  first: string | null;
  last: string | null;
  user_role: string | null;
  organization_id: number | null;
  is_active: boolean;
  user_status: string | null;
}

export interface SupportDebugUserSearchResponse {
  items: SupportDebugUserSearchResult[];
  page?: number;
  total_pages?: number;
  total_items?: number;
}

// GET /master-data/users/<id> detail. CAUTION: unlike the list endpoint
// (snake_case), the detail model marshals MIXED casing — role/isActive/
// userStatus are camelCase while last_payday/pay_frequency stay snake_case.
// These names mirror the wire exactly (master-data user_details_model).
export interface SupportDebugUserDetail {
  id: string;
  email: string;
  first: string | null;
  last: string | null;
  role: string | null;
  isActive?: boolean | null;
  userStatus?: string | null;
  last_payday: string | null; // "YYYY-MM-DD"
  pay_frequency: string | null;
  organization: { id: number; name: string } | null;
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

// Partial (ilike) email search. Deliberately NOT UsersService.fetchSearchedUsersList,
// which force-scopes results to an organization_id.
export const searchUsersByEmail = async (
  email: string
): Promise<SupportDebugUserSearchResponse> => {
  const query = new URLSearchParams();
  query.append("page", "1");
  query.append("per_page", "10");
  query.append("email", email.trim().toLowerCase());
  return http.get(`/master-data/users?${query.toString()}`);
};

export const fetchUserDetail = async (userId: string): Promise<SupportDebugUserDetail> =>
  http.get(`/master-data/users/${userId}`);

export const fetchUpcomingBills = async (
  userId: string,
  days?: number
): Promise<UpcomingBillsResponse> => {
  const query = new URLSearchParams();
  query.append("userId", userId);
  if (days != null) query.append("days", String(days));
  return http.get(`${BASE_PATH}/upcoming-bills?${query.toString()}`);
};

export const fetchSafeToSpend = async (userId: string): Promise<SafeToSpendResponse> => {
  const query = new URLSearchParams();
  query.append("userId", userId);
  return http.get(`${BASE_PATH}/safe-to-spend?${query.toString()}`);
};

export const fetchCashflowShortfall = async (
  userId: string,
  asOfDate?: string
): Promise<CashflowShortfallResponse> => {
  const query = new URLSearchParams();
  query.append("userId", userId);
  if (asOfDate) query.append("asOfDate", asOfDate);
  return http.get(`${BASE_PATH}/cashflow-shortfall?${query.toString()}`);
};
